/**
 * CobrandHeader — Fixed co-branding header bar (agency + Chutt...)
 * Visible on ALL pages when user is admin_agence.
 * z-index 200 (above FloatingNav at 150).
 * Contains: Superadmin (if superadmin), Logout actions. Settings moved to FloatingNav.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { colors } from '../../styles/colors';

function lightenColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (num >> 16) + Math.round(255 * percent / 100));
  const g = Math.min(255, ((num >> 8) & 0x00FF) + Math.round(255 * percent / 100));
  const b = Math.min(255, (num & 0x0000FF) + Math.round(255 * percent / 100));
  return `rgb(${r},${g},${b})`;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

function CobrandHeader({
  agencyName = '',
  agencyLogoUrl = '',
  accentColor = colors.primary,
  userName = '',
  isSuperadmin = false,
  onLogout,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const color1 = accentColor;
  const color2 = lightenColor(accentColor, 10);
  const color3 = lightenColor(accentColor, 20);

  // White opacity helpers (white text/overlays on colored gradient background)
  const whiteAlpha = (a) => `rgba(255,255,255,${a})`;

  const iconBtnSx = {
    color: whiteAlpha(0.6),
    '&:hover': { color: colors.white, bgcolor: whiteAlpha(0.1) },
    transition: 'all 0.2s',
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        height: '44px',
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'space-between',
        background: `linear-gradient(135deg, ${color1} 0%, ${color2} 60%, ${color3} 100%)`,
        padding: '0 32px',
        boxSizing: 'border-box',
        maxWidth: '100vw',
        overflow: 'hidden',
        boxShadow: `0 2px 16px rgba(${parseInt(accentColor.slice(1, 3), 16)},${parseInt(accentColor.slice(3, 5), 16)},${parseInt(accentColor.slice(5, 7), 16)},0.2)`,
      }}
    >
      {/* Left side: Logo | Separator | Agency name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0, flexShrink: 1 }}>
        {agencyLogoUrl ? (
          <>
            <img
              src={agencyLogoUrl}
              alt={agencyName || 'Agency'}
              style={{
                height: 26,
                width: 'auto',
                filter: 'brightness(10)',
                objectFit: 'contain',
              }}
            />
            <Box
              sx={{
                width: '1px',
                height: '20px',
                background: whiteAlpha(0.2),
                mx: '4px',
              }}
            />
          </>
        ) : null}
        <Typography
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.82rem',
            fontWeight: 600,
            color: colors.white,
            letterSpacing: '0.03em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {agencyName}
        </Typography>
      </Box>

      {/* Right side: Actions | Avatar + Name | Separator | "powered by Chutt..." */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
        {/* Action buttons: Superadmin, Logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', mr: 1 }}>
          {isSuperadmin && (
            <Tooltip title="Superadmin">
              <IconButton
                size="small"
                aria-label="Superadmin"
                onClick={() => navigate('/superadmin')}
                sx={{
                  ...iconBtnSx,
                  color: location.pathname === '/superadmin' ? colors.gold : whiteAlpha(0.6),
                }}
              >
                <AdminPanelSettingsIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Deconnexion">
            <IconButton
              size="small"
              aria-label="Deconnexion"
              onClick={onLogout}
              sx={{ ...iconBtnSx, '&:hover': { color: colors.dangerLight, bgcolor: whiteAlpha(0.1) } }}
            >
              <LogoutIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Separator */}
        <Box
          sx={{
            width: '1px',
            height: '20px',
            background: whiteAlpha(0.2),
            mx: '6px',
          }}
        />

        {/* Avatar circle */}
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: whiteAlpha(0.15),
            border: `1.5px solid ${whiteAlpha(0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.68rem',
              color: colors.white,
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {getInitials(userName)}
          </Typography>
        </Box>

        {/* User name */}
        <Typography
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.72rem',
            color: whiteAlpha(0.85),
            fontWeight: 500,
            ml: '8px',
            lineHeight: 1,
          }}
        >
          {userName}
        </Typography>

        {/* Separator */}
        <Box
          sx={{
            width: '1px',
            height: '20px',
            background: whiteAlpha(0.2),
            mx: '4px',
          }}
        />

        {/* "powered by Chutt..." */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.65rem',
              color: whiteAlpha(0.7),
              lineHeight: 1,
            }}
          >
            powered by
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Dancing Script", cursive',
              fontSize: '0.85rem',
              color: whiteAlpha(0.85),
              lineHeight: 1,
            }}
          >
            Chutt...
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default CobrandHeader;
